import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "../../lib/cn"

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-primary focus-visible:ring-offset-0 focus-visible:border-brand-primary disabled:pointer-events-none disabled:opacity-60",
    {
        variants: {
            variant: {
                // focus-visible:ring-white: the base's brand-primary inset ring is
                // invisible on this variant's own brand-primary fill — a WCAG 2.4.7
                // failure found by the 2026-08-07 P3 review and fixed app-wide here
                // by operator ruling (UI-37). White stays visible on both the primary
                // fill and the desaturated disabled fill.
                primary: "bg-brand-primary text-white border border-transparent hover:bg-brand-primary-hover focus-visible:ring-white",
                secondary: "bg-surface-base text-text-primary border border-border-default hover:bg-surface-raised",
                ghost: "bg-transparent text-text-primary border border-transparent hover:bg-surface-raised",
                danger: "bg-state-danger text-white border border-transparent hover:bg-state-danger-hover",
                outline: "bg-surface-canvas text-text-primary border border-border-default hover:border-text-subtle",
                "outline-danger": "bg-surface-base text-state-danger border border-state-danger hover:bg-state-danger-subtle",
                icon: "p-0 bg-transparent border border-border-default text-text-primary hover:bg-surface-raised",
            },
            size: {
                default: "min-h-[44px] px-4",
                sm: "min-h-[36px] px-3 text-xs",
                lg: "min-h-[48px] px-8 text-base",
                // Toolbar control (Sort/Filters/Map): tighter padding, lg radius, normal
                // weight. Replaces the per-call inline override on the Directory/Favourites
                // toolbars while keeping the 44px tap target.
                compact: "min-h-[44px] px-3 py-2 rounded-lg h-auto font-normal",
                icon: "min-h-[40px] min-w-[40px] [&>svg]:w-[18px] [&>svg]:h-[18px]",
                "icon-dense": "min-h-[36px] min-w-[36px] text-xs [&>svg]:w-4 [&>svg]:h-4",
            },
            fullWidth: {
                true: "w-full",
            },
            // Active/pressed treatment for toolbar toggle buttons (e.g. the mobile map
            // toggle on the outline variant): a raised white surface + shadow.
            pressed: {
                true: "bg-surface-base text-text-primary border-border-default shadow-lg",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
            fullWidth: false,
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
    isLoading?: boolean | undefined
    intent?: string // Legacy prop for transition
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, intent, size, fullWidth, pressed, asChild = false, isLoading, disabled, children, ...props }, ref) => {
        const Comp = asChild ? Slot : "button"

        // Handle legacy intent → variant mapping
        const resolvedVariant = (variant || intent || "primary") as any;

        return (
            <Comp
                className={cn(buttonVariants({ variant: resolvedVariant, size: resolvedVariant === 'icon' && size !== 'icon-dense' ? 'icon' : size, fullWidth, pressed, className }))}
                ref={ref}
                {...props}
                // After the spread, and with `disabled` destructured out of props —
                // otherwise a caller passing BOTH isLoading and disabled had its raw
                // `disabled` win via later-prop-wins, making isLoading unable to
                // disable the button mid-flight (found by the 2026-08-07 P3 review:
                // DeleteAccountModal could double-fire its delete during the await).
                disabled={isLoading || disabled}
            >
                {/* Radix Slot (asChild) requires exactly ONE child element, so we must
                    NOT also emit the loading spinner alongside it — pass the single child
                    straight through. The spinner only applies to the real <button>. */}
                {asChild ? (
                    children
                ) : (
                    <>
                        {isLoading && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
                        {children}
                    </>
                )}
            </Comp>
        )
    }
)
Button.displayName = "Button"

export { Button, buttonVariants }
